import { Injectable } from "@angular/core";
import { catchError, concatMap, map, Observable, of, tap } from "rxjs";
import { BaseService } from "./base.service";
import { AngularFirestore } from "@angular/fire/compat/firestore";
import { HttpClient } from "@angular/common/http";
import { AngularFireFunctions } from "@angular/fire/compat/functions";
import { FireLoggingService } from "./fire-logging.service";
import { UserService } from "./user.service";
import { DNS_CONFIG_COLLECTION, OP_EQUALS } from "../../app.constants";
import { environment } from "../../../environments/environment";
import { AuthFirebaseService } from "./auth-firebase.service";

@Injectable({
    providedIn: 'root',
})
export class DnsConfigService extends BaseService {
    loginCompanyLogo: string;
    copContentLogo: string;

    constructor(
        protected override firestore: AngularFirestore,
        protected override http: HttpClient,
        protected override functions: AngularFireFunctions,
        protected userService: UserService,
        protected override fireLogging: FireLoggingService,
        private auth: AuthFirebaseService
    ) {
        super(firestore, http, functions, fireLogging);
    }

    handleThemes() {
        let observable = of({});
        const [companyDomain, finsteinDomain] =
            window.location.hostname.split('.');

        // Verification required for production dns,
        // avoids having to consult the topic for "beratungsprozess" before the finstein domain
        //
        // Example:
        //
        // if the hostname is "beratungsprozess.finstein.de"
        // the companyDomain value is beratungsprozess
        // and the finsteinDomain is finstein
        //
        // Alternatively if the hostname is "exemple.beratungsprozess.finstein.de"
        // the companyDomain is example and the finsteinDomain changes to beratungsprozess
        if (companyDomain && finsteinDomain != 'finstein') {
            observable = this.getCompanyTheme(companyDomain);
        }

        return observable;
    }

    getCompanyTheme(subdomain: string): Observable<any> {
        return this.firestore
            .collection(DNS_CONFIG_COLLECTION, (ref) => {
                return ref.where('subdomain', OP_EQUALS, subdomain);
            })
            .snapshotChanges()
            .pipe(
                map((actions) =>
                    actions.map((action) => {
                        const data: any = action.payload.doc.data();
                        data.id = action.payload.doc.id;
                        return data;
                    })
                ),
                map((data: any) => {
                    const theme = {
                        ...data[0]?.theme?.baseColors,
                        ...data[0]?.theme?.assetsColors,
                        ...data[0]?.theme?.customBackgroundColor,
                        ...data[0]?.theme?.componentsColors,
                        ...data[0]?.theme?.tableColors,
                        ...data[0]?.theme?.sliderColors,
                    };

                    if (
                        this.userService.loggedUser?.companyId &&
                        this.userService.loggedUser.companyId !==
                            data[0]?.companyId
                    ) {
                        this.removeTheme(theme);
                    }

                    this.applyTheme(theme);
                    return {
                        copContentLogo: data[0]?.copContentLogo,
                        loginCompanyLogo: data[0]?.loginCompanyLogo,
                    };
                }),
                    catchError((error) => {
                        const message = error.error
                            ? error.error.message
                            : error.message;
                        this.fireLogging
                            .sendErrorLog(`[getCompanyThemes(dns-config.service.ts)] An error
                    occurred while getting item from collection ${DNS_CONFIG_COLLECTION}, details: ${
                            message || JSON.stringify(error)
                        }`);
                        return of({});
                    })
            );
    }

    applyTheme(theme: any) {
        Object.keys(theme).forEach((variable) => {
            document.documentElement.style.setProperty(
                `--${variable}`,
                theme[variable]
            );
        });
    }

    removeTheme(theme: any) {
        Object.keys(theme).forEach((variable) => {
            document.documentElement.style.removeProperty(`--${variable}`);
        });
    }
}
